/**
 * /datasets — every dataset, and "New dataset" (`?new=1`, optionally `&agent=`):
 * a CSV upload, or a live dataset pulled from traces (`?new=traces&name=`).
 * Active and Archived tabs (`?view=archived`); each row's ⋯ archives or restores it.
 */

import { Link, useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { EmptyState, QueryState } from "@/components/ui/states";
import { formatCount, formatDate } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { useDatasets } from "./hooks";
import { LiveDatasetDialog } from "./live/live-dataset-dialog";
import { isLive, versionPeriod } from "./live/version-period";
import { NewDatasetDialog } from "./new-dataset-dialog";
import type { Dataset } from "./types";
import { archiveColumn } from "@/features/archive/archive-menu";
import { ArchiveEmpty, ArchiveTabs, ArchivedMatches } from "@/features/archive/archived";
import { useArchiveTabs } from "@/features/archive/hooks";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";

const COLUMNS: Column<Dataset>[] = [
  {
    header: "Dataset",
    cell: (d) => (
      <span className="flex items-center gap-2">
        <Link to={`/datasets/${d.id}`} className="font-semibold hover:underline">
          {d.name}
        </Link>
        {d.is_golden && <Pill tone="gold">Golden</Pill>}
        {isLive(d) && <Pill tone="run">Live</Pill>}
      </span>
    ),
  },
  { header: "Items", num: true, cell: (d) => formatCount(d.latest_version?.item_count ?? 0) },
  {
    header: "Version",
    className: "font-mono",
    cell: (d) =>
      d.latest_version ? (
        <>
          v{d.latest_version.version}
          {versionPeriod(d.latest_version) && (
            <span className="font-sans text-muted"> · {versionPeriod(d.latest_version)}</span>
          )}
        </>
      ) : (
        <span className="text-muted">{isLive(d) ? "no run yet" : "empty"}</span>
      ),
  },
  {
    header: "Updated",
    className: "text-muted",
    cell: (d) => formatDate(d.latest_version?.created_at ?? d.created_at),
  },
  archiveColumn("dataset"),
];

const matching = (rows: Dataset[] | undefined, q: string) =>
  (rows ?? []).filter((d) => d.name.toLowerCase().includes(q.toLowerCase()));

export function DatasetsPage() {
  const { can } = useProject();
  const url = useUrlState();
  const tabs = useArchiveTabs("dataset", useDatasets());
  const datasets = tabs.query;
  const q = url.get("q") ?? "";
  const rows = matching(datasets.data, q);
  const archived = tabs.counts.archived ?? 0;
  const navigate = useNavigate();
  const open = () => url.set({ new: 1 });
  const close = () => url.set({ new: null, agent: null, name: null });
  const newButton = (
    <ProjectAction permission="datasets:write">
      <Button variant="primary" onClick={open}>
        New dataset
      </Button>
    </ProjectAction>
  );

  return (
    <>
      <PageHeader
        title="Datasets"
        subtitle="Questions to test agents on, with the answers you expect. Each experiment records the version it used."
        actions={
          <ProjectAction permission="datasets:write">
            <Button variant="primary" onClick={open}>
              <Plus size={14} strokeWidth={2.2} aria-hidden />
              New dataset
            </Button>
          </ProjectAction>
        }
      />
      <ArchiveTabs kind="dataset" {...tabs.counts} />
      <input
        className="inp w-64 max-w-full"
        type="search"
        placeholder="Find a dataset"
        aria-label="Find a dataset"
        value={q}
        onChange={(e) => url.set({ q: e.target.value }, { replace: true })}
      />
      <Box>
        <QueryState query={datasets} what="datasets">
          {rows.length ? (
            <DataTable columns={COLUMNS} rows={rows} />
          ) : q ? (
            <EmptyState title="No datasets match.">
              {tabs.view === "active" ? <ArchivedMatches kind="dataset" q={q} /> : "Try another word."}
            </EmptyState>
          ) : tabs.view === "archived" || archived > 0 ? (
            <ArchiveEmpty kind="dataset" view={tabs.view} archived={archived} create={newButton} />
          ) : (
            <EmptyState title="Create your first dataset" action={newButton}>
              A dataset is a list of inputs with expected outputs. Upload a CSV (there is a sample file to
              start from), or pull production traces from Langfuse or qFlow.
            </EmptyState>
          )}
        </QueryState>
      </Box>
      {can("datasets:write") && url.get("new") === "traces" ? (
        <LiveDatasetDialog
          initialName={url.get("name") ?? ""}
          onClose={close}
          onCreated={(d) => navigate(`/datasets/${d.id}`)}
        />
      ) : (
        can("datasets:write") &&
        url.get("new") && (
          <NewDatasetDialog
            agentId={url.get("agent")}
            onPullFromTraces={(name) => url.set({ new: "traces", agent: null, name }, { replace: true })}
            onClose={close}
          />
        )
      )}
    </>
  );
}
